import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { extent as olExtent } from 'openlayers';
import WKT from 'ol/format/WKT';
import { MultiPolygon, Polygon } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import { Feature } from 'ol';
import { Coordinate } from 'ol/coordinate';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';

import { getYValue } from '../utils/wellsUtils';
import { useSessionStore } from 'session/useSessionStore';
import { ISurveyMetadata } from 'features/seismic/models/interfaces/ISurveyMetadata';
import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';
import { useMapStore } from 'features/seismic/stores/useMapStore';
import { useAPI } from 'hooks/useAPI';
import { useLine3DNavigationStore } from '../stores/useLine3DNavigationStore';
import { GeopostSeismicLayer } from '../models/classes/GeopostSeismicLayer';
import { SurveyType } from '../models/enums/SurveyType';
import { useLoadingTileStore } from '../stores/useLoadingTilesStore';
import { GeopostMap } from '../models/classes/GeopostMap';
import { IColorbar } from '../models/interfaces/IColorbar';
import { GeopostSeismicDebugLayer } from '../models/classes/GeopostSeismicDebugLayer';
import { getParamsFromUrl } from '../utils/seismicUrlUtils';
import { SeismicCalculatorFactory } from '../models/classes/SeismicCalculatorFactory';
import { IGeopostSeismicLayerParams } from '../models/interfaces/IGeopostSeismicLayerParams';
import { LineType } from '../models/enums/LineType';
import { useGetRequirements } from '../components/projections/api/useSeismicController';
import { useTileSize } from './useTileSize';
import { usePerformanceMonitoringStore } from '../stores/usePerformanceMonitoringStore';
import { useFeatureFlags } from './useFeatureFlags';
import { getMetadataPerformanceDataFromAxiosResponse } from '../utils/performanceUtils';
import { usePrepareRender } from './usePrepareRender';
import { IErrorResponse } from '../models/interfaces/IErrorResponse';
import { IResponseAPI } from 'models/interfaces/IResponseAPI';
import { GeopostTileLoadingLayer } from '../models/classes/layers/GeopostTileLoadingLayer';
import { ZSliceTileSourceStrategy } from '../models/classes/TileSourceStrategies/ZSliceTileSourceStrategy';
import { LineTileSourceStrategy } from '../models/classes/TileSourceStrategies/LineTileSourceStrategy';
import { calculateXPosition, CalculateXPositionWithIncrement, getSurveyExtentDistance, getTrace, getXByTrace, getYByAmplitude } from '../utils/calculateScreenValuesUtils';
import { useGetSliceRotation } from './useGetSliceRotation';
import { useGetResolutionsImperatively } from '../api/useSeismicResolutionController';
import { MeasurementType } from '../models/enums/MeasurementType';
import { SampleIntervalUnit } from '../models/enums/SampleIntervalUnity';
import { useVolumeDebugLayer } from './useVolumeDebugLayer';
import { DecimationType } from '../models/enums/DecimationType';

interface IMetadataResponse extends IErrorResponse {
    Metadata: ISurveyMetadata;
    Resolutions: number[];
    TileDimensions: number[][];
    Colorbar: IColorbar
}

function createSeismicMetadataError (): ISurveyMetadata{
    return {
        Error: true,
        Attributes: [],
        DisplayName: '',
        Domain: 0,
        Delay: 0,
        EBCDIC: '',
        IndexDate: '',
        IsIndexed: false,
        MaxSampleValue: 0,
        MinSampleValue: 0,
        Size: 0,
        SurveyToken: '',
        Type: 0,
        VolumePath: '',
        VolumeToken: '',
        DimensionNames: {
            Inline: '',
            Xline: '',
            Sample: '',
            Cdp: ''
        },
        Header: {
            AverageTraceDistance: 0,
            ByteSize: 0,
            FormatCode: 0,
            Length: 0,
            NumberOfTraces: 0,
            SampleInterval: 0,
            SampleIntervalUnit: 0,
            SamplesPerTrace: 0,
            TotalY: 0
        },
        Entitlement3DLimits: {
            InlineStart: 0,
            InlineEnd: 0,
            XlineStart: 0,
            XlineEnd: 0,
            TotalInlines: 0,
            TotalXlines: 0,
            EntitlementGeomSrid: 0
        },
        LoadingParams: {
            CoordinateX: 0,
            CoordinateY: 0,
            Inline: 0,
            ScalarCoordinateX: 0,
            ScalarCoordinateY: 0,
            ScalarX: 0,
            ScalarY: 0,
            SRID: 0,
            Xline: 0,
            CoordinateSystem: ''
        },
        Survey3DInfo: {
            EntitledInlines: [],
            EntitledXlines: [],
            InlineEnd: 0,
            InlineIncrement: 0,
            InlineStart: 0,
            TotalInlines: 0,
            TotalXlines: 0,
            XlineEnd: 0,
            XlineIncrement: 0,
            XlineStart: 0,
            EntitledInlineStart: 0,
            EntitledInlineEnd: 0,
            EntitledXlineStart: 0,
            EntitledXlineEnd: 0,
            TotalEntitledInlines: 0,
            TotalEntitledXlines: 0
        },
        Survey2DInfo: {
            ShotPointIncrement: 0,
            ShotPointStart: 0,
            ShotPointEnd: 0,
        },
        AttributeCalculationMethod: 0,
        DefaultScaleX: 1,
        DefaultScaleY: 1,
        DefaultIsFlipped: false,
        Datum: 1,
        MeasurementType: MeasurementType.Depth,
        IsVelocity: false,
        DecimationType: DecimationType.None,
    };
}

export function useStartMap(containerMap: RefObject<HTMLDivElement>) {
    const urlApi = '/Seismic/Volume/Metadata';
    const { tenantConfig, jwtToken } = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration,
        jwtToken: state.jwtToken
    }));

    const { current: urlParams } = useRef(getParamsFromUrl());

    const setMetadataPerformanceData = usePerformanceMonitoringStore(state => state.setMetadataPerformanceData);
    const addTilePerformanceData = usePerformanceMonitoringStore(state => state.addTilePerformanceData);
    const addTileErrorData = usePerformanceMonitoringStore(state => state.addTilesWithError);

    const [error, setError] = useState('');

    const { execute: executeMainMetadata } = useAPI<IMetadataResponse>(urlApi);

    const { mutateAsync: getResolutions } = useGetResolutionsImperatively();

    const { setIsLoading: setSeismicIsLoading, scale, setSortOption, setScaleX, setScaleY, setAbsoluteGain, setSurveyMetadata, setVolumeToken, rangeTrace: stateRangeTrace, setRangeTrace,
        setColorbar, setCalculator: setSeismicCalculator, setRestrict, isInverted, setIsInverted, setVolumeIdentifier, isAntialiasApplied, setBaseCalculatedAttributeId,
        setRestrictedAreaLayer, surveyMetadata, seismicCalculator, traceRange } = useSeismicStore(state => ({
        rangeTrace: state.rangeTrace,
        setSortOption: state.setSortOption,
        scale: state.scale,
        setScaleX: state.setScaleX,
        setScaleY: state.setScaleY,
        isInverted: state.isInverted,
        setIsInverted: state.setIsInverted,
        isAntialiasApplied: state.isAntialiasApplied,
        setAbsoluteGain: state.setAbsoluteGain,
        setCalculator: state.setCalculator,
        setColorbar: state.setColorbar,
        setIsLoading: state.setIsLoading,
        setSurveyMetadata: state.setSurveyMetadata,
        setVolumeToken: state.setVolumeToken,
        setRangeTrace: state.setRangeTrace,
        setRestrict: state.setRestrict,
        setVolumeIdentifier: state.setVolumeIdentifier,
        setUseVelocityColorbars: state.setUseVelocityColorbars,
        setBaseCalculatedAttributeId: state.setBaseCalculatedAttributeId,
        setRestrictedAreaLayer: state.setRestrictedAreaLayer,
        surveyMetadata: state.surveyMetadata,
        seismicCalculator: state.calculator,
        traceRange: state.rangeTrace
    }));

    const { lineTypeFromStore, setLineNumber, setLineType } = useLine3DNavigationStore(state => ({
        lineTypeFromStore: state.lineType,
        setLineNumber: state.setLineNumber,
        setLineType: state.setLineType
    }));

    const { dpi, jpgQuality, totalParallelProcess, maxTilesLoading, pixelRatio, initialZoom, zoomFactor,
        setLayers, setMap, setResolutions, pointerMoveObserver, mainLayer, setBackgroundColor } = useMapStore(state => ({
        dpi: state.dpi,
        pointerMoveObserver: state.pointerMoveObserver,
        initialZoom: state.initialZoom,
        jpgQuality: state.jpgQuality,
        maxTilesLoading: state.maxTilesLoading,
        pixelRatio: state.pixelRatio,
        setLayers: state.setLayers,
        setMap: state.setMap,
        setResolutions: state.setResolutions,
        totalParallelProcess: state.totalParallelProcess,
        zoomFactor: state.zoomFactor,
        mainLayer: state.mainSeismicLayer,
        setBackgroundColor: state.setBackgroundColor,
    }));

    const { mutateAsync: getRequirements } = useGetRequirements();

    const { increaseLoaded, increaseLoading, updatePercentage } = useLoadingTileStore(state => ({
        increaseLoaded: state.increaseLoaded,
        increaseLoading: state.increaseLoading,
        updatePercentage: state.updatePercentage
    }));

    const seismic2DDefaults = useSessionStore(state => state.tenantConfiguration?.viewer2DDefaults);

    const tileSize = useTileSize();

    const prepareRender = usePrepareRender();
    const isPerformanceMonitoringEnabledByFlag = useFeatureFlags().featureFlags?.seismic2D.PerformanceMonitor;
    const { current: isPerformanceMonitoringEnabledByParam } = useRef(urlParams.performanceMonitoring);
    const isPerformanceMonitoringEnabled = useMemo(() => isPerformanceMonitoringEnabledByParam, [isPerformanceMonitoringEnabledByParam]);

    const mapStartedRef = useRef(false);

    const isInvertedRef = useRef(false);

    const handleTileRequestDebugData = useMemo(() => {
        if (urlParams.performanceMonitoring) {
            return (xhr: XMLHttpRequest, lineNumber: number, tileWidth: number, requestSendAt: Date) => {
                if (!!xhr.response) {
                    const queryApiTimeHeader = xhr.getResponseHeader('x-gp-debug-api-query-time');
                    const requestCameOnServerAtHeader = xhr.getResponseHeader('x-gp-debug-request-came-at');
                    const responseLeaveServerAtHeader = xhr.getResponseHeader('x-gp-debug-response-leave-at');
                    const plotTimeHeader = xhr.getResponseHeader('x-gp-debug-tile-plot-time');

                    const startSample = xhr.getResponseHeader('start-sample');
                    const endSample = xhr.getResponseHeader('end-sample');
                    const startTrace = xhr.getResponseHeader('start-trace');
                    const endTrace = xhr.getResponseHeader('end-trace');
                    const geopostSource = xhr.getResponseHeader('Geopost-Source');
                    const traceId = xhr.getResponseHeader('Geopost-Trace-Id');

                    if (!queryApiTimeHeader || !requestCameOnServerAtHeader || !responseLeaveServerAtHeader || !plotTimeHeader || !startSample || !endSample || !startTrace || !endTrace) {
                        console.log('A tile request doesn\'t have the required debug headers');
                        return;
                    }

                    const plotTime = parseFloat(plotTimeHeader);
                    const queryApiTime = parseFloat(queryApiTimeHeader);
                    const requestCameOnServerAt = new Date(requestCameOnServerAtHeader);
                    const responseLeaveServerAt = new Date(responseLeaveServerAtHeader);
                    const currentTime = new Date().getTime();
                    const processingTime = ((responseLeaveServerAt.getTime() - requestCameOnServerAt.getTime()) / 1000 - queryApiTime);
                    const requestNetworkTime = (requestCameOnServerAt.getTime() - requestSendAt.getTime()) / 1000;
                    const responseNetworkTime = (currentTime - responseLeaveServerAt.getTime()) / 1000;
                    const totalTime = (currentTime - requestSendAt.getTime()) / 1000;

                    addTilePerformanceData({
                        traceId: traceId ?? '',
                        source: geopostSource ?? '',
                        apiTime: parseFloat(queryApiTime.toFixed(3)),
                        lineNumber: lineNumber,
                        lineType: urlParams.lineType ?? lineTypeFromStore,
                        serverProcessingTime: parseFloat((processingTime - plotTime).toFixed(3)),
                        requestNetworkTime: parseFloat(requestNetworkTime.toFixed(3)),
                        responseNetworkTime: parseFloat(responseNetworkTime.toFixed(3)),
                        plotTime: plotTime,
                        totalTime: parseFloat(totalTime.toFixed(3)),
                        tileWidth: tileWidth,
                        startSample: parseFloat(startSample),
                        endSample: parseFloat(endSample),
                        startTrace: parseFloat(startTrace),
                        endTrace: parseFloat(endTrace)
                    });
                }
            };
        }
    }, [urlParams.performanceMonitoring, urlParams.lineType, addTilePerformanceData, lineTypeFromStore]);

    const handleTileRequestError = (xhr: XMLHttpRequest, lineNumber: number, tileWidth: number, startTrace: number, endTrace: number) => {
        const fileReader = new FileReader();
        fileReader.onloadend = (e) => {
            if (!e.target?.result){
                return;
            }

            const error = JSON.parse(e.target.result.toString()) as IResponseAPI<IErrorResponse>;

            if (error) {
                addTileErrorData({
                    errorMessage: error['Result']['ErrorName'] + ' - ' + error['Result']['Error'],
                    status: xhr.status,
                    lineNumber,
                    lineType: urlParams.lineType ?? lineTypeFromStore,
                    tileWidth,
                    startTrace,
                    endTrace,
                    fullErrorMessage: error.Result.Traceback
                });
            }
        };

        if (typeof xhr.response === 'string' || !xhr.response){
            try {
                const error = JSON.parse(xhr.response) as IResponseAPI<IErrorResponse>;

                if (error) {
                    addTileErrorData({
                        errorMessage: error['Result']['ErrorName'] + ' - ' + error['Result']['Error'],
                        status: xhr.status,
                        lineNumber,
                        lineType: urlParams.lineType ?? lineTypeFromStore,
                        tileWidth,
                        startTrace,
                        endTrace,
                        fullErrorMessage: error.Result.Traceback
                    });
                }
            }
            catch (e){
                addTileErrorData({
                    errorMessage: 'Unknown error',
                    status: xhr.status,
                    lineNumber,
                    lineType: urlParams.lineType ?? lineTypeFromStore,
                    tileWidth,
                    startTrace,
                    endTrace,
                    fullErrorMessage: 'Unknown error'
                });
            }
        }
        else {
            fileReader.readAsText(xhr.response);
        }
    };

    const preparingCacheRef = useRef(false);

    const featureFlags = useFeatureFlags().featureFlags?.seismic2D;

    const getSliceRotation = useGetSliceRotation();

    const handleTileRequestSuccess = useCallback((xhr: XMLHttpRequest, lineNumber: number, tileWidth: number, requestSendAt: Date) => {
        if (!urlParams.forceDisableSamplesCache && !!tenantConfig?.useSamplesMatrixCache && !!surveyMetadata && !!seismicCalculator && !featureFlags?.DisableSamplesCache) {
            const geopostSourceHeader = xhr.getResponseHeader('geopost-source');
            if (!!geopostSourceHeader && geopostSourceHeader.toLowerCase() === 'fly' && !!mainLayer && !preparingCacheRef.current) {
                const layerParams = mainLayer.getParams();
                preparingCacheRef.current = true;
                if (!urlParams.disablePrepareRenderRetry) {
                    prepareRender(mainLayer, layerParams, layerParams.loadingLayer!, false, lineTypeFromStore, false);
                }
                setTimeout(() => { preparingCacheRef.current = false }, 120000);
            }
        }
        if (!!handleTileRequestDebugData) {
            handleTileRequestDebugData(xhr, lineNumber, tileWidth, requestSendAt);
        }
    }, [handleTileRequestDebugData, lineTypeFromStore, mainLayer, prepareRender, urlParams.forceDisableSamplesCache]);

    useEffect(() => {
        if (!!mainLayer) {
            mainLayer.tileSourceStrategy.onSettleTileRequest = handleTileRequestSuccess;
        }
    }, [handleTileRequestSuccess, mainLayer]);

    useEffect(() => {
        const rectMap = containerMap.current?.getBoundingClientRect()!;
        const height = parseInt(rectMap.height.toString());
        const width = parseInt(rectMap.width.toString());

        if (!jwtToken) {
            //setSeismicIsLoading(false);
            return;
        }

        async function start() {
            setSeismicIsLoading(true);
            const requestLeaveAt = new Date();

            const lineTypeParam = urlParams.lineType;

            const response = await executeMainMetadata({
                params: {
                    filePath: urlParams.filePath,
                    volumeToken: urlParams.volumeToken,
                    forceDisableCache: urlParams.forceDisableImageCache,
                    tileWidth: tileSize.width,
                    tileHeight: tileSize.height,
                    screenHeight: height,
                    includePercentageCalc: true,
                    calcResolutions: (lineTypeParam ?? lineTypeFromStore) !== LineType.ZSlice,
                    volumeIdentifier: urlParams.volumeIdentifier,
                    traceFieldFilter: urlParams.traceFieldFilter,
                    traceFieldValue: urlParams.traceFieldValue,
                    defaultSort : urlParams.defaultSort
                },
                headers: {
                    'x-gp-debug-active': urlParams.performanceMonitoring,
                    'x-gp-debug-request-leave-at': new Date().toUTCString()
                }
            }, (response) => {
                if (urlParams.performanceMonitoring) {
                    const performanceData = getMetadataPerformanceDataFromAxiosResponse(response, requestLeaveAt, urlParams.volumeToken);
                    if (!!performanceData) {
                        setMetadataPerformanceData(performanceData);
                    }
                }
            }, (error) => {
                let messageErrorUser = 'A critical error ocurred';

                if (error instanceof AxiosError){
                    const axiosError = error as AxiosError<IResponseAPI<IMetadataResponse>>;

                    const response = axiosError.response?.data?.Result;

                    if (response){
                        if (response.Error === 'Seismic not found'){
                            messageErrorUser = 'No file was found for the provided seismic.';
                        }
                    }

                    if (urlParams.performanceMonitoring) {
                        const performanceData = getMetadataPerformanceDataFromAxiosResponse(axiosError.response!, requestLeaveAt, urlParams.volumeToken);
                        if (!!performanceData) {
                            setMetadataPerformanceData(performanceData);
                        }
                    }
                }
                else {
                    setMetadataPerformanceData({
                        error: error.message,
                        apiTime: 0,
                        requestNetworkTime: 0,
                        responseNetworkTime: 0,
                        serverProcessingTime: 0,
                        totalTime: 0,
                        volumeToken: ''
                    });
                }

                setError(messageErrorUser);
                setSurveyMetadata(createSeismicMetadataError());
            });

            setVolumeIdentifier(urlParams.volumeIdentifier);

            if (response) {
                if (response.Error) {
                    setSurveyMetadata(createSeismicMetadataError());
                    setError(response.Error);
                }
                else {
                    const { needsFlip, rotation, needsVerticalFlip, reference: rotationReference } = await getSliceRotation(lineTypeFromStore, response.Metadata);
                    const restrict = await getRequirements({
                        surveyToken: response?.Metadata.SurveyToken,
                        tenantToken: urlParams.tenantToken,
                        volumeToken: urlParams.volumeToken
                    });

                    //only for PGS for testing
                    if (tenantConfig?.tenantToken === '238506eb-3664-4fe2-8a58-3996e5534163') {
                        if (restrict.BlockProfileVisualization) {
                            setError('Profile Visualization operation not allowed.');
                            window.location.href = window.location.protocol + '//' + window.location.host + '/map';
                        }
                    }

                    setRestrict(restrict);
                    const metadata = response.Metadata;

                    const scaleX = metadata.DefaultScaleX > 0 ? metadata.DefaultScaleX : scale.x;
                    const scaleY = metadata.DefaultScaleY > 0 ? metadata.DefaultScaleY : scale.y;
                    const applyInvert = !!metadata.DefaultIsFlipped;

                    const { lineNumber: lineNumberParam, lineType: lineTypeParam } = urlParams;

                    let lineType = lineTypeFromStore;

                    if (metadata?.Type === SurveyType.Seismic2D){
                        lineType = LineType.Line2D;
                    }

                    const seismicCalculator = SeismicCalculatorFactory.buildCalculator(metadata.IsIndexed, lineType, metadata.Type);
                    const rangeTrace = seismicCalculator.getInitialRangeTrace(metadata);

                    const lineIncrement = seismicCalculator.getLineIncrement(metadata);
                    const lineNumber = lineNumberParam ?? seismicCalculator.calculateLineStart(metadata);
                    const extent = seismicCalculator.calculateExtent(metadata, { x: scaleX, y: scaleY }, rangeTrace);
                    const center = seismicCalculator.calculateCenter(extent, metadata, { x: scaleX, y: scaleY }, rangeTrace);

                    const totalTraces = lineType === LineType.Xline ? metadata.Survey3DInfo.TotalInlines : metadata.Survey3DInfo.TotalXlines;
                    const traceSkip = seismicCalculator.getSkipTrace(metadata);
                    let resolutions = response.Resolutions;
                    let tileDimensions = response.TileDimensions;
                    if (!resolutions || !tileDimensions) {
                        ({ Resolutions: resolutions,  TileDimensions: tileDimensions} = await getResolutions({
                            scaleY: scaleY,
                            screenHeight: height,
                            tileWidth: tileSize.width,
                            traceSkip: traceSkip,
                            tileHeight: tileSize.height,
                            totalSamples: lineType !== LineType.ZSlice ? metadata.Header.SamplesPerTrace : rotation !== 0 ? getSurveyExtentDistance(metadata, scaleX, scaleY) : metadata.Survey3DInfo.TotalXlines,
                            totalTraces: totalTraces,
                        }));
                    }

                    const restrictedAreaGeometry = !!metadata.RestrictedAreaWKT ? new WKT().readGeometry(metadata.RestrictedAreaWKT) as MultiPolygon : undefined;

                    const samplesCacheSystem = urlParams.samplesCacheSystem;
                    const imageCacheSystem = urlParams.imageCacheSystem;
                    const skipTraceCount = urlParams.skipTraceCount;
                    const sampleInterval = metadata.Header.SampleIntervalUnit === SampleIntervalUnit.Microsecond ? metadata.Header.SampleInterval / 1000 : metadata.Header.SampleInterval;

                    let layerParams: IGeopostSeismicLayerParams = {
                        tenantToken: tenantConfig?.tenantToken,
                        sourceApi: urlParams.sourceApi,
                        urlRender: tenantConfig?.endpoints.render,
                        byteSize: metadata.Header.ByteSize,
                        colorbar: response.Colorbar.Name,
                        dpi: dpi,
                        extent: extent,
                        filePath: metadata.VolumePath,
                        isIndexedVolume: metadata.IsIndexed,
                        jpgQuality: jpgQuality,
                        maxSampleValue: metadata.MaxSampleValue,
                        minSampleValue: metadata.MinSampleValue,
                        parallelProcesses: totalParallelProcess,
                        resolutions: resolutions,
                        tileDimensionsPerResolution: tileDimensions,
                        scaleX: scaleX,
                        scaleY: scaleY,
                        skipTraceCount: skipTraceCount,
                        tileHeight: tileSize.height,
                        tileWidth: tileSize.width,
                        volumeToken: metadata.VolumeToken,
                        applyWatermark: restrict?.SeismicWatermark ?? false,
                        surveyInlineStart: metadata.Survey3DInfo.InlineStart,
                        surveyInlineEnd: metadata.Survey3DInfo.InlineEnd,
                        surveyXlineStart: metadata.Survey3DInfo.XlineStart,
                        surveyXlineEnd: metadata.Survey3DInfo.XlineEnd,
                        surveyInlineIncrement: metadata.Survey3DInfo.InlineIncrement,
                        surveyXlineIncrement: metadata.Survey3DInfo.XlineIncrement,
                        numberOfTraces: metadata.Header.NumberOfTraces,
                        absoluteGain: {
                            min: metadata.MinSampleValue,
                            max: metadata.MaxSampleValue
                        },
                        debug: urlParams.debug,
                        increaseLoaded: increaseLoaded,
                        increaseLoading: increaseLoading,
                        resetLoadingControl: () => {
                            updatePercentage(0,0);
                        },
                        accessToken: jwtToken,
                        forceDisableCache: urlParams.forceDisableImageCache,
                        sampleInterval: Math.ceil(sampleInterval),
                        baseSampleInterval: Math.ceil(sampleInterval),
                        inlineDimensionName: metadata.DimensionNames?.Inline,
                        xlineDimensionName: metadata.DimensionNames?.Xline,
                        sampleDimensionName: metadata.DimensionNames?.Sample,
                        cdpDimensionName: metadata.DimensionNames?.Cdp,
                        samplesCacheSystem,
                        imageCacheSystem,
                        isInverted: needsFlip !== applyInvert /* <---- OR Exclusivo */,
                        isAntialiasApplied: isAntialiasApplied,
                        renderLimits: [],
                        lineNumber: lineNumber,
                        volumeSamplesPerTrace: metadata.Header.SamplesPerTrace,
                        verticallyInverted: needsVerticalFlip,
                        attributeCalculationMethod: metadata.AttributeCalculationMethod,
                        restrictedAreaGeometry: restrictedAreaGeometry,
                        opacity: 1,
                        prepareRenderChunkSize: tenantConfig?.prepareRenderChunkSize ?? 128,
                        baseInlineStart: metadata.Survey3DInfo.InlineStart,
                        baseInlineEnd: metadata.Survey3DInfo.InlineEnd,
                        baseXlineStart: metadata.Survey3DInfo.XlineStart,
                        baseXlineEnd: metadata.Survey3DInfo.XlineEnd,
                        baseInlineIncrement: metadata.Survey3DInfo.InlineIncrement,
                        baseXlineIncrement: metadata.Survey3DInfo.XlineIncrement,
                    };

                    if (featureFlags?.DisableSamplesCache || urlParams.forceDisableSamplesCache || !tenantConfig?.useSamplesMatrixCache) {
                        layerParams.renderLimits?.push([-Infinity, +Infinity]);
                    }

                    if (rangeTrace !== null) {
                        layerParams.traceRangeStart = rangeTrace.initial;
                        layerParams.traceRangeEnd = rangeTrace.end;
                    }

                    setAbsoluteGain({
                        min: metadata.MinSampleValue,
                        max: metadata.MaxSampleValue,
                    });

                    const loadingLayer = new GeopostTileLoadingLayer();

                    const swipeLoadingLayer = new GeopostTileLoadingLayer();

                    const overlayLoadingLayer = new GeopostTileLoadingLayer();

                    const tileSourceStrategy = lineType === LineType.ZSlice ?
                        new ZSliceTileSourceStrategy(tenantConfig!.endpoints.render, metadata.Survey3DInfo.XlineStart!, metadata.Survey3DInfo.XlineIncrement!)
                        :
                        new LineTileSourceStrategy(lineType, tenantConfig!.endpoints.render, handleTileRequestError);

                    const [ surveyLineStart, surveyLineEnd ] = tileSourceStrategy.getSurveyLineDimensions(layerParams);

                    const layer = new GeopostSeismicLayer({
                        ...layerParams,
                        loadingLayer: loadingLayer
                    }, tileSourceStrategy, isPerformanceMonitoringEnabled);

                    const overlay = new GeopostSeismicLayer({
                        ...layerParams,
                        loadingLayer: overlayLoadingLayer,
                    }, tileSourceStrategy, isPerformanceMonitoringEnabled);
                    overlay.setOpacity(0.15);
                    overlay.setVisible(false);
                    const map = new GeopostMap({
                        maxZoom: restrict?.SeismicMaxZoomLevel ?? false,
                        center: center,
                        extent: extent,
                        resolutions: resolutions,
                        maxTilesLoading: maxTilesLoading,
                        pixelRatio: pixelRatio,
                        zoom: initialZoom,
                        zoomFactor: zoomFactor
                    });

                    const swipe = new GeopostSeismicLayer({
                        ...layerParams,
                        isSwiped: true,
                        swipeValue: 50,
                        geopostMap: map,
                        loadingLayer: swipeLoadingLayer,
                    }, tileSourceStrategy, isPerformanceMonitoringEnabled);
                    swipe.setVisible(false);

                    layer.map = map;
                    overlay.map = map;
                    swipe.map = map;
                    map.addLayer(layer);
                    map.addLayer(swipe);
                    map.addLayer(overlay);

                    map.addLayer(loadingLayer);
                    map.addLayer(swipeLoadingLayer);
                    map.addLayer(overlayLoadingLayer);

                    map.getView().setRotation(rotation);
                    console.log('ROTATION ====>', rotation);

                    map.on('rendercomplete', () => {
                        layer.clearLoadingBarriers();
                        console.log('render complete, clearing loading stuff from layer');
                    });

                    map.on('moveend', () => {
                        const settledZoom = map.getView().getConstrainedZoom(map.getView().getZoom(), 0) ?? 0;
                        loadingLayer.clearErrorsByZoom(settledZoom);
                        swipeLoadingLayer.clearErrorsByZoom(settledZoom);
                        overlayLoadingLayer.clearErrorsByZoom(settledZoom);

                        layer.evictStaleErrorTiles();
                        swipe.evictStaleErrorTiles();
                        overlay.evictStaleErrorTiles();
                    });

                    let debugLayer: GeopostSeismicDebugLayer | null = null;
                    if (urlParams.debug) {
                        debugLayer = new GeopostSeismicDebugLayer(layer.tileGrid!);
                        map.addLayer(debugLayer);
                    }

                    setLayers(layer, debugLayer, overlay, swipe);
                    setMap(map, width, height);
                    setResolutions(resolutions);
                    setSurveyMetadata(metadata);
                    setVolumeToken(metadata.VolumeToken);

                    if (stateRangeTrace.end > metadata.Header?.NumberOfTraces) {
                        setRangeTrace(stateRangeTrace.initial, metadata.Header?.NumberOfTraces);
                    }
                    if (metadata.IsIndexed) {
                        /*const viewExtent = map.getView().calculateExtent(map.getSize());
                        const minTrace = seismicCalculator.getMinTrace(metadata);
                        const startTrace = getTrace(viewExtent[0], scale.x, minTrace, lineIncrement);
                        const endTrace = getTrace(viewExtent[2], scale.x, minTrace, lineIncrement);
                        */
                        setRangeTrace(1, metadata.Header.NumberOfTraces);
                    }

                    setColorbar(response.Colorbar);
                    setSeismicCalculator(seismicCalculator);
                    if (lineType === LineType.Line2D) {
                        setLineType(lineType);
                    }
                    setLineNumber(lineNumber);
                    setSeismicIsLoading(false);

                    setScaleX(scaleX);
                    setScaleY(scaleY);

                    let initialSort = urlParams?.defaultSort;
                    if (initialSort && initialSort !== ''){
                        setSortOption(initialSort);
                    }

                    setIsInverted(applyInvert);
                    setBaseCalculatedAttributeId(metadata.AttributeCalculationMethod);

                    if (metadata.AttributeCalculationMethod === 1) {
                        setBackgroundColor('#FFFFFF');
                    }

                }
            }
        }

        if (!mapStartedRef.current) {
            mapStartedRef.current = true;
            start();
        }
    }, [jwtToken]);

    return {
        error: error
    };
}